import React, { useState, useEffect } from "react"
import { makeStyles } from "@material-ui/styles"
import {
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableHead as MuiTableHead,
  TableCell as MuiTableCell,
  TableRow as MuiTableRow,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { EditOutlined } from "@material-ui/icons"
import { SpeedDialAction } from "@material-ui/lab"
import { useParams, NavLink, useHistory } from "react-router-dom"
import InfiniteScroll from "react-infinite-scroll-component"
import useAsyncEffect from "use-async-effect"
import fileDownload from "js-file-download"
import { parse } from "json2csv"
import {
  AreaHeader,
  Content,
  Checkbox,
  MainHeading,
  OrganisationLogo,
  SearchInput,
  LoadingSpinner,
  FloatingSpeedDial,
  Icon,
  UserStatusChip,
  PersonViewer,
  PersonCreator,
  MultiplePersonCreator,
  NoItemsMessage,
  DisplayLimiter,
  RowBox,
  HelpBanner,
  PersonTrainingStatus,
  PersonAccreditationStatus,
  DesktopPeopleButton,
  UserAvatar,
  FiltersPanel,
  TooltipIcon,
  useSnackbar,
  TimeAgo,
  ColumnBox,
  Caption,
} from "../../components"
import {
  useLazyQueryAdminableUsers,
  useQueryAdminableUserCounts,
  useMutationUpdateUsers,
  USER_STATUS,
  useMutationResendInvites,
  useLazyQueryAdminableUser,
} from "../../data"
import { useAuth } from "../../services"
import { toId } from "../../utils"

const useStyles = makeStyles((theme) => ({
  fullName: {
    fontSize: 16,
    color: theme.palette.text.tinted,
    lineHeight: "20px",
  },
  roles: {
    color: theme.palette.text.light,
    whiteSpace: "nowrap",
  },
  tableHead: {
    position: "sticky",
    top: theme.dimensions.header.height,
    backgroundColor: "white",
    zIndex: 1000,
  },
  tableCellCenter: {
    textAlign: "center",
  },
  infiniteScrollWrapper: {
    "&&>div": {
      display: "contents",
    },
  },
  infiniteScroll: {
    display: "contents",
  },
  supportInfo: {
    fontSize: 20,
    cursor: "pointer",
  },
  supportInfoRef: {
    display: "flex",
    alignSelf: "center",
  },
}))

const initialState = {
  filters: [
    { type: "status", value: "active" },
    { type: "status", value: "invited" },
  ],
}

const compileFilters = (searchText, filters) => ({
  searchText,
  locations: [],
  roles: [],
  groups: [],
  status: [],
  trainingStatus: [],
  ...filters.reduce((acc, filter) => {
    acc[filter.type] = [...(acc[filter.type] || []), filter.value]
    return acc
  }, {}),
})

const People = () => {
  const { id, action } = useParams()
  const history = useHistory()
  const classes = useStyles()
  const snackbar = useSnackbar()
  const [loadAdminableUsers, { data, loading: adminableUsersLoading, refetch, loadMore }] = useLazyQueryAdminableUsers()
  const { data: countsData, refetch: refetchCounts } = useQueryAdminableUserCounts()
  const [loadAdminableUser] = useLazyQueryAdminableUser()
  const theme = useTheme()
  const isInline = useMediaQuery(theme.breakpoints.down("md"))
  const {
    settings: { organisation },
    hasFeature,
  } = useAuth()

  const [updateUsers] = useMutationUpdateUsers()
  const [resendInvites] = useMutationResendInvites()

  const [callRefetch, setCallRefetch] = useState(false)
  const [openSpeedDial, setOpenSpeedDial] = useState(false)
  const [all, setAll] = useState(false)
  const [some, setSome] = useState(false)
  const [checked, setChecked] = useState({})
  const [checkedCount, setCheckedCount] = useState(0)
  const [items, setItems] = useState([])
  const [view, setView] = useState(null)
  const [edit, setEdit] = useState(null)

  const [searchText, setSearchText] = useState("")
  const [searchTextLoading, setSearchTextLoading] = useState(false)
  const [filters, setFilters] = useState(initialState.filters)

  const [bulkAction, setBulkAction] = useState("")
  const [bulkActionParams, setBulkActionParams] = useState([])

  useEffect(() => {
    if (data?.adminableUsers) {
      const users = data.adminableUsers.map((user) => ({ ...user }))
      setChecked((prev) => {
        users.forEach((user) => (prev[toId(user)] = prev[toId(user)] || false))
        return { ...prev }
      })
      setItems([...users])
    }
  }, [data, id, action])

  useAsyncEffect(async () => {
    await loadAdminableUsers({
      variables: {
        ...compileFilters(searchText, filters),
      },
    })
    if (searchTextLoading) {
      setSearchTextLoading(false)
    }
  }, [filters, loadAdminableUsers, searchText])

  useEffect(() => {
    if (callRefetch) {
      setCallRefetch(false)
      refetch({
        ...compileFilters(searchText, filters),
      })
      refetchCounts()
    }
  }, [callRefetch, filters, refetch, refetchCounts, searchText])

  useAsyncEffect(async () => {
    if (id && !id.startsWith("new") && data) {
      let user = data.adminableUsers.find((u) => toId(u) === id)
      if (!user) {
        const loadResult = await loadAdminableUser({ variables: { id } })
        if (loadResult) {
          user = loadResult.data.adminableUser
        }
      }
      if (user) {
        if (action === "edit") {
          setView(null)
          setEdit(user)
        } else {
          setView(user)
        }
      }
    }
  }, [action, data, id])

  useEffect(() => {
    const newAll = items.every((item) => checked[toId(item)])
    if (newAll !== all) {
      setAll(newAll)
    }
  }, [all, checked, items])

  useEffect(() => {
    const newSome = !items.every((item) => checked[toId(item)]) && items.some((item) => checked[toId(item)])
    if (some !== newSome) {
      setSome(newSome)
    }
  }, [checked, items, some])

  useEffect(() => {
    const newCheckedCount = items.filter((item) => checked[toId(item)]).length
    setCheckedCount(newCheckedCount)
  }, [checked, items])

  const hasTraining = hasFeature("training")
  const hasAccreditation = hasFeature("accreditation")

  const handleToggleSpeedDial = () => {
    setOpenSpeedDial(!openSpeedDial)
  }

  const handleCreate = (type) => {
    if (type === "single") history.push("/people/new")
    if (type === "multiple") history.push("/people/new-multiple")
  }

  const handleClose = (isBack) => {
    if (!view && !edit) {
      setCallRefetch(true)
      return history.push("/people")
    }
    if (view) {
      setView(null)
      return history.push("/people")
    }
    if (edit) {
      const editId = toId(edit)
      setEdit(null)
      if (isBack) {
        return history.push(`/people/${editId}`)
      }
      return history.push("/people")
    }
  }

  const handleChangeSearchText = (text) => {
    setSearchTextLoading(true)
    setSearchText(text)
    clearBulkAction()
  }

  const handleBulkActionChange = (event) => {
    setBulkAction(event.target.value)
    setBulkActionParams([])
  }

  const handleBulkActionParamsChange = (event) => {
    setBulkActionParams(event.target.value)
  }

  const handleBulkActionApply = async () => {
    const ids = items.filter((item) => checked[toId(item)]).map((item) => item.id)
    switch (bulkAction) {
      case "inactive":
      case "active":
        await updateUsers({
          variables: { ids, status: bulkAction === "active" ? USER_STATUS.ACTIVE : USER_STATUS.INACTIVE },
        })
        break
      case "invites":
        await resendInvites({ variables: { ids } })
        break
      default:
        await updateUsers({ variables: { ids, [bulkAction]: bulkActionParams } })
    }
    clearBulkAction()
  }

  const clearBulkAction = () => {
    if (bulkAction) setBulkAction("")
    if (bulkActionParams.length) setBulkActionParams([])
    handleUncheckAll()
  }

  const handleDownload = () => {
    const trainingFields = hasTraining
      ? ["trainingStatus", "trainingPercentComplete", "trainingDueAt", "trainingLastActiveAt"]
      : []

    const fields = [
      "id",
      "firstName",
      "lastName",
      "email",
      "roles",
      "locations",
      "areasOfWork",
      ...trainingFields,
      "status",
      "activeAt",
    ]
    const selectedItems = items
      .filter((item) => checked[toId(item)])
      .map(({ roles, locations, groups, training, ...rest }) => ({
        roles: roles.length ? roles.map((item) => item.friendlyName).join(", ") : "User",
        locations: locations.map((item) => item.name).join(", "),
        areasOfWork: groups.map((item) => item.name).join(", "),
        trainingStatus: training.status,
        trainingPercentComplete: training.percentComplete,
        trainingDueAt: training.dueAt,
        trainingLastActiveAt: training.lastActiveAt,
        ...rest,
      }))

    try {
      const csv = parse(selectedItems, { fields })
      fileDownload(csv, "download.csv", "text/csv")
    } catch (error) {
      snackbar.showMessage({ message: error.message, color: "secondary", icon: <Icon name="download-failed" /> })
    }
  }

  const handleCheckAll = () => {
    if (items) {
      setChecked((prev) => {
        if (all) items.forEach((user) => (prev[toId(user)] = false))
        else items.forEach((user) => (prev[toId(user)] = true))
        return { ...prev }
      })
    }
  }

  const handleUncheckAll = () => {
    if (items) {
      setChecked((prev) => {
        items.forEach((user) => (prev[toId(user)] = false))
        return { ...prev }
      })
    }
  }

  const handleCheckUser = (user) => {
    const newChecked = !checked[toId(user)]
    setChecked((prev) => {
      prev[toId(user)] = newChecked
      return { ...prev }
    })
  }

  const handleFilterChange = (type, values) => {
    setFilters((prev) => {
      const next = prev.filter((filter) => filter.type !== type)
      next.push(...values.map((value) => ({ type, value })))
      return next
    })
    handleUncheckAll()
  }

  const handleClearFilters = () => {
    setFilters([])
    handleUncheckAll()
  }

  const handleFilterDelete = ({ type, value }) => {
    setFilters((prev) => prev.filter((filter) => !(filter.type === type && filter.value === value)))
    handleUncheckAll()
  }

  const handleTrainingClick = (user) => {
    history.push(`/people/${toId(user)}/training`)
  }

  const handleAccreditationsClick = (user) => {
    history.push(`/people/${toId(user)}/accreditation`)
  }

  const any = all || some

  const loading = adminableUsersLoading

  return (
    <DisplayLimiter>
      {view && <PersonViewer onClose={handleClose} user={view} isInline={isInline} />}

      {edit && <PersonCreator onClose={handleClose} edit={edit} isInline={isInline} />}

      {id === "new" && <PersonCreator onClose={handleClose} isInline={isInline} />}

      {id === "new-multiple" && <MultiplePersonCreator onClose={handleClose} isInline={isInline} />}

      {(!isInline || (isInline && !id)) && (
        <>
          <AreaHeader
            title="People"
            titleIcon="people-light"
            desktopButtons={<DesktopPeopleButton onCreate={handleCreate} />}
            mobileButtons={
              !id && (
                <FloatingSpeedDial
                  onClose={handleToggleSpeedDial}
                  onOpen={handleToggleSpeedDial}
                  open={openSpeedDial}
                  data-cy="Button-create-person"
                >
                  <SpeedDialAction
                    tooltipTitle="Single person"
                    icon={<Icon name="person" />}
                    tooltipOpen
                    onClick={() => handleCreate("single")}
                  />
                  <SpeedDialAction
                    tooltipTitle="Multiple people"
                    icon={<Icon name="people" />}
                    tooltipOpen
                    onClick={() => handleCreate("multiple")}
                  />
                </FloatingSpeedDial>
              )
            }
          />

          <Content full start>
            <HelpBanner name="people" />

            <Box mb={4}>
              <RowBox mb={4}>
                <Box>
                  <OrganisationLogo />
                </Box>
                <Box>
                  <Box ml={1}>
                    <MainHeading mb={0} ml={0}>
                      {organisation.name}
                    </MainHeading>
                  </Box>
                  <Box ml={1}>
                    {countsData && <>{countsData.adminableUserCounts.active} active employees</>}
                    {!countsData && <>Loading...</>}
                  </Box>
                </Box>
              </RowBox>

              <SearchInput
                placeholder="Search people"
                debounce={300}
                onChange={handleChangeSearchText}
                loading={searchTextLoading}
              />

              <FiltersPanel
                filters={filters}
                onChange={handleFilterChange}
                onClear={handleClearFilters}
                onDelete={handleFilterDelete}
                any={!loading && any}
                bulkAction={bulkAction}
                bulkActionParams={bulkActionParams}
                itemCount={checkedCount}
                onBulkActionApply={handleBulkActionApply}
                onBulkActionChange={handleBulkActionChange}
                onBulkActionParamsChange={handleBulkActionParamsChange}
                onDownload={handleDownload}
              />

              {loading && !data && (
                <Box display="flex" justifyContent="center">
                  <LoadingSpinner size={60} />
                </Box>
              )}
              {data && (
                <>
                  <Table data-cy="People-table">
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          {items?.length > 0 && (
                            <Checkbox
                              color="primary"
                              checked={all || some}
                              onClick={handleCheckAll}
                              indeterminate={some}
                            />
                          )}
                        </TableCell>
                        <TableCell>Name &amp; Role</TableCell>
                        <TableCell>Locations</TableCell>
                        <TableCell>Areas of Work</TableCell>
                        {hasTraining && <TableCell className={classes.tableCellCenter}>Training</TableCell>}
                        {hasAccreditation && <TableCell className={classes.tableCellCenter}>Accreditations</TableCell>}
                        <TableCell className={classes.tableCellCenter}>Status</TableCell>
                        <TableCell className={classes.tableCellCenter}>Edit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody className={classes.infiniteScrollWrapper}>
                      <InfiniteScroll
                        dataLength={items?.length || 0}
                        next={loadMore}
                        hasMore
                        className={classes.infiniteScroll}
                      >
                        {Boolean(items?.length) &&
                          items.map((user) => {
                            const operandio = user.email?.endsWith("@operandio.com")

                            return (
                              <TableRow key={user.id}>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    checked={checked[toId(user)]}
                                    onClick={() => handleCheckUser(user)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <NavLink to={`/people/${toId(user)}`}>
                                    <Box display="flex" flexDirection="row">
                                      <UserAvatar {...user} />
                                      <Box ml={1}>
                                        <Box className={classes.fullName}>{user.fullName}</Box>
                                        <Box className={classes.roles}>
                                          {!operandio && user.roles.map((role) => role.friendlyName).join(", ")}
                                          {!operandio && user.roles.length === 0 && "User"}
                                          {operandio && (
                                            <RowBox>
                                              <Box mr={0.5}>Operandio Support</Box>
                                              <TooltipIcon
                                                name="information"
                                                tooltip="This user does not count towards your billing"
                                                className={classes.supportInfo}
                                                refClassName={classes.supportInfoRef}
                                              />
                                            </RowBox>
                                          )}
                                        </Box>
                                      </Box>
                                    </Box>
                                  </NavLink>
                                </TableCell>
                                <TableCell>{user.locations.map((location) => location.name).join(", ")}</TableCell>
                                <TableCell>{user.groups.map((group) => group.name).join(", ")}</TableCell>
                                {hasTraining && (
                                  <TableCell className={classes.tableCellCenter}>
                                    <PersonTrainingStatus user={user} onClick={handleTrainingClick} />
                                  </TableCell>
                                )}
                                {hasAccreditation && (
                                  <TableCell className={classes.tableCellCenter}>
                                    <PersonAccreditationStatus user={user} onClick={handleAccreditationsClick} />
                                  </TableCell>
                                )}
                                <TableCell className={classes.tableCellCenter}>
                                  <ColumnBox alignItems="center">
                                    <UserStatusChip user={user} />
                                    {Boolean(user.activeAt) && (
                                      <Caption mt={0.5} mb={0} tiny>
                                        <TimeAgo date={user.activeAt} compact />
                                      </Caption>
                                    )}
                                  </ColumnBox>
                                </TableCell>
                                <TableCell className={classes.tableCellCenter}>
                                  <IconButton
                                    component={NavLink}
                                    to={`/people/${toId(user)}`}
                                    data-cy={`Person-edit-${toId(user)}`}
                                  >
                                    <EditOutlined />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                      </InfiniteScroll>
                    </TableBody>
                  </Table>

                  {!loading && items && !items.length && <NoItemsMessage>No matching items</NoItemsMessage>}
                </>
              )}
            </Box>
          </Content>
        </>
      )}
    </DisplayLimiter>
  )
}

const Table = (props) => <MuiTable component="div" {...props} />
const TableBody = (props) => <MuiTableBody component="div" {...props} />
const TableHead = (props) => <MuiTableHead component="div" {...props} />
const TableCell = (props) => <MuiTableCell component="div" {...props} />
const TableRow = (props) => <MuiTableRow component="div" {...props} />

export default People
export { compileFilters as compileAdminableUsersFilters }
